import { getUrl } from '../config';
import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Outlet, useLocation } from "react-router-dom";
import axios from 'axios';

import { Box, CssBaseline, Paper as MuiPaper, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";

import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import useDashboardItems from "../components/sidebar/dashboardItems";
import dashboardItems2 from "../components/sidebar/dashboardItemsOther";
import Sidebar from "../components/sidebar/Sidebar";
import Footer from "../components/Footer";
import useGlobalMessaging from "../hooks/useGlobalMessaging";
import { SocketProvider } from "../contexts/SocketContext";
import ModalChangelog from "../components/linar/Modal/ModalChangelog";
import ModalIncomingTemplate from "../components/linar/Modal/ModalIncomingTemplate";
import useAuth from "../hooks/useAuth";

const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.5; }
`;

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
  overflow-x: hidden;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const SplitView = ({ onClose }) => {
  const [erps, setErps] = useState([]);
  const [selectedErpId, setSelectedErpId] = useState(() => {
    return sessionStorage.getItem('splitRightErpId') || '';
  });
  const [leftWidth, setLeftWidth] = useState(50); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    const fetchErps = async () => {
      try {
        const response = await axios.get(getUrl() + "/api/erps");
        if (response.data.status === 'success') {
          setErps(response.data.data);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des ERPs:", error);
      }
    };
    fetchErps();
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const newWidth = (e.clientX / window.innerWidth) * 100;
      if (newWidth > 15 && newWidth < 85) {
        setLeftWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const selectErp = (erpId) => {
    setSelectedErpId(erpId);
    sessionStorage.setItem('splitRightErpId', erpId);
  };

  const currentErpId = localStorage.getItem("erpId") || "";
  
  // Left Frame URL: current path inside left frame
  const leftUrl = window.location.pathname + window.location.search + 
    (window.location.search ? '&' : '?') + 'erpId=' + currentErpId;
  
  // Right Frame URL: current path inside right frame, or dashboard fallback
  const rightUrl = selectedErpId ? (window.location.pathname + window.location.search + 
    (window.location.search ? '&' : '?') + 'erpId=' + selectedErpId) : '';

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: theme.palette.background.default,
      zIndex: 99999,
      boxSizing: 'border-box'
    }}>
      {/* Split Control Header */}
      <Box sx={{
        height: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: '#1b2635',
        zIndex: 10,
        boxSizing: 'border-box'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', animation: `${pulse} 2s infinite` }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff' }}>
            Mode Double ERP Actif
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          color="error" 
          size="small" 
          onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: '8px', px: 2 }}
        >
          Quitter le mode double ERP
        </Button>
      </Box>

      {/* Pane Container */}
      <Box sx={{ flex: 1, display: 'flex', width: '100%', position: 'relative', overflow: 'hidden' }}>
        {/* Left Pane */}
        <Box sx={{ 
          width: `${leftWidth}%`, 
          height: '100%', 
          position: 'relative',
          overflow: 'hidden',
          pointerEvents: isDragging ? 'none' : 'auto'
        }}>
          <iframe 
            src={leftUrl} 
            title="ERP Gauche" 
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        </Box>

        {/* Resizer Divider */}
        <Box 
          onMouseDown={handleMouseDown}
          sx={{
            width: '8px',
            height: '100%',
            cursor: 'col-resize',
            bgcolor: isDragging ? 'primary.main' : (theme.palette.mode === 'dark' ? '#1b2635' : '#e0e0e0'),
            position: 'relative',
            transition: 'background-color 0.2s',
            zIndex: 5,
            '&:hover': {
              bgcolor: 'primary.main',
              '& .drag-handle': {
                bgcolor: 'primary.main',
                transform: 'translate(-50%, -50%) scale(1.1)'
              }
            }
          }}
        >
          {/* Grab Handle */}
          <Box 
            className="drag-handle"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '24px',
              height: '40px',
              borderRadius: '20px',
              bgcolor: theme.palette.mode === 'dark' ? '#2c3e50' : '#ffffff',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              pointerEvents: 'none'
            }}
          >
            {/* Grip lines */}
            <Box sx={{ display: 'flex', gap: '3px' }}>
              <Box sx={{ width: '2px', height: '14px', bgcolor: theme.palette.text.secondary, borderRadius: '1px' }} />
              <Box sx={{ width: '2px', height: '14px', bgcolor: theme.palette.text.secondary, borderRadius: '1px' }} />
            </Box>
          </Box>
        </Box>

        {/* Right Pane */}
        <Box sx={{ 
          width: `${100 - leftWidth}%`, 
          height: '100%', 
          position: 'relative',
          overflow: 'hidden',
          pointerEvents: isDragging ? 'none' : 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {selectedErpId ? (
            <iframe 
              src={rightUrl} 
              title="ERP Droite" 
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          ) : (
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 4,
              textAlign: 'center',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(10, 18, 30, 0.4)' : 'rgba(245, 247, 250, 0.4)',
            }}>
              <Box sx={{ maxWidth: 500, width: '100%' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary }}>
                  Sélectionner le second ERP
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
                  Choisissez l'ERP sur lequel vous souhaitez travailler dans le panneau de droite.
                </Typography>
                
                {/* List of ERP cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, justifyContent: 'center' }}>
                  {erps.map((erp) => (
                    <MuiPaper
                      key={erp.erpId}
                      elevation={0}
                      onClick={() => selectErp(erp.erpId)}
                      sx={{
                        p: 3,
                        cursor: 'pointer',
                        borderRadius: '12px',
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                          borderColor: 'primary.main',
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                        }
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {erp.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'primary.main', display: 'block', mt: 1 }}>
                        ID: {erp.erpId}
                      </Typography>
                    </MuiPaper>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Dashboard = ({ children }) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [user, setUser] = React.useState([]);
  const [loadingUser, setLoadingUser] = useState(true);  // Ajout de l'état loadingUser
  const [changelogUpdates, setChangelogUpdates] = useState([]);
  const [changelogModalOpen, setChangelogModalOpen] = useState(false);
  const { user: authUser } = useAuth();

  const [splitMode, setSplitMode] = useState(() => {
    return sessionStorage.getItem('splitMode') === 'true';
  });

  useEffect(() => {
    const handleToggle = () => {
      setSplitMode(prev => {
        const newValue = !prev;
        sessionStorage.setItem('splitMode', newValue ? 'true' : 'false');
        if (!newValue) {
          sessionStorage.removeItem('splitRightErpId');
        }
        return newValue;
      });
    };
    window.addEventListener('toggleSplitMode', handleToggle);
    return () => {
      window.removeEventListener('toggleSplitMode', handleToggle);
    };
  }, []);

  const handleCloseSplit = () => {
    setSplitMode(false);
    sessionStorage.setItem('splitMode', 'false');
    sessionStorage.removeItem('splitRightErpId');
    window.location.reload();
  };

  const isSubFrame = window.self !== window.top;

  const dashboardItems = useDashboardItems();

  // Initialiser la messagerie globale
  const { socket, isConnected } = useGlobalMessaging();



  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const fetchUser = async () => {
    try {
      const response = await axios.get(getUrl() + `/api/user/getMe`);
      if (response.data.status === 'success') {
        setUser(response.data.user);
      } else {
        console.error('Erreur lors de la récupération des Users: ', response.data.message);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des Users :", error);
    } finally {
      setLoadingUser(false); // Une fois les données récupérées, on arrête le loading
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUnreadUpdates = async () => {
    try {
      // Ne pas afficher le modal pour l'utilisateur dev (id 14)
      if (authUser?.id === 14) {
        return;
      }
      const response = await axios.get(getUrl() + '/api/changelogs/unread', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      if (response.data.status === 'success') {
        if (response.data.data.length > 0) {
          console.log('Mises à jour non lues reçues:', response.data.data.length);
          setChangelogUpdates(response.data.data);
          setChangelogModalOpen(true);
        } else {
          // S'il n'y a plus de mises à jour non lues, fermer le modal
          console.log('Aucune mise à jour non lue');
          setChangelogUpdates([]);
          setChangelogModalOpen(false);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des mises à jour:', error);
    }
  };

  useEffect(() => {
    if (authUser && !loadingUser) {
      fetchUnreadUpdates();
    }
  }, [authUser, loadingUser]);

  // Réafficher le modal à chaque changement de page si des mises à jour non lues existent
  useEffect(() => {
    if (authUser && !loadingUser && authUser.id !== 14) {
      // Vérifier à nouveau les mises à jour non lues à chaque changement de page
      fetchUnreadUpdates();
    }
  }, [location.pathname]);

  const handleChangelogClose = () => {
    setChangelogModalOpen(false);
    // Ne pas recharger immédiatement pour éviter de rouvrir le modal
    // Le rechargement se fera au prochain changement de page ou au prochain chargement
  };

  const handleMarkAsRead = () => {
    setChangelogUpdates([]);
    // Recharger les mises à jour non lues après marquage pour s'assurer qu'on a les bonnes données
    fetchUnreadUpdates();
  };

  if (splitMode && !isSubFrame) {
    return <SplitView onClose={handleCloseSplit} />;
  }

  return (
    <SocketProvider socket={socket} isConnected={isConnected}>
      <Root>
        <CssBaseline />
        <GlobalStyle />
        {!open && !loadingUser && (  // On n'affiche la sidebar que si les données sont chargées
          <Drawer>
            <Box sx={{ display: { xs: "block", lg: "none" } }}>
              <Sidebar
                PaperProps={{
                  style: {
                    width: "100vw",           // pleine largeur sur mobile
                    maxWidth: "100vw",
                  },
                }}
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                items={user.country === -1 ? null : (user.country === 2 || user.country === 3 ? dashboardItems2 : dashboardItems)}
              />
            </Box>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Sidebar
                PaperProps={{ style: { width: drawerWidth } }}
                items={user.country === -1 ? null : (user.country === 2 || user.country === 3 ? dashboardItems2 : dashboardItems)}
              />
            </Box>
          </Drawer>
        )}
        <AppContent>
          <Navbar onDrawerToggle={handleDrawerToggle} onOpenToggle={handleOpen} />
          <MainContent p={isLgUp ? 12 : 5}>
            {children}
            <Outlet />
          </MainContent>
          <Footer />
        </AppContent>
        <ModalIncomingTemplate />
        <ModalChangelog
          open={changelogModalOpen}
          onClose={handleChangelogClose}
          updates={changelogUpdates}
          onMarkAsRead={handleMarkAsRead}
        />
      </Root>
    </SocketProvider>
  );
};

export default Dashboard;